<docs>
A user/organization image, falling back to initials on an orange placeholder. Built on Reka `Avatar`:
a src that 404s or is slow shows the initials instead of a broken image and swaps to the photo once
it loads (no fallback delay, so a slow image briefly shows initials). Display only: no v-model, no
click handling; wrap it in your own button or link. ZoneAvatarStack renders a row of overlapping
avatars with a "+N" pill; ZoneIndicator is the corner dot itself; ZoneTooltip the hover label.

## Notes

- The `<img>` stays in the DOM while loading (Reka uses `v-show`), so in jsdom, where the probe image
  never loads, both the img and the initials fallback are present. Assert on the presence of the
  `<img>`, not on the absence of the fallback.
- Initials are first + last initial ("John Michael J. Fox Doe" → "JD"), a single letter for a
  one-word name, and an en dash when there is no name. A nameless avatar is `aria-hidden` rather
  than announcing that dash.
- The box does not clip its children: the corner indicator is allowed to overhang. The corner radius
  is applied to the image and the fallback as well as the box.
- The corner dot follows one rule per shape, not a per-size table. On a round avatar its centre sits
  on the circumference on the bottom-right diagonal, so half of it hangs outside and its white ring
  bites into the face; on a square one it stays flush with the bottom edge and hangs a quarter of
  itself off the right. Its size barely moves (6px on the two small avatars, 8px on the two big
  ones), because a dot scaled proportionally would cover a quarter of a 24px face. Neither is a
  prop: an avatar owns its own corner.
- Zone::Avatar (Ruby) differs in three ways, deliberately; Figma is the source of truth here.
  `shape: round|square` replaces `variant: rounded|squared` (Ruby's "rounded" meant the full circle,
  which reads backwards next to ZonePill's `shape` and ZoneButton's `rounded`); initials use Figma's
  12/12/16/30px at medium weight, not Ruby's 10/12/14/20px at semibold; a one-word name yields one
  letter, not a doubled one. Figma calls the shapes "Circle" / "Rounded" and `base` "Regular".

Figma: [Avatar](https://www.figma.com/design/DD0eEumGwzGfrPbb7q2iak/-Zone--Tokens---Components?node-id=535-231)
</docs>

<template>
  <div
    class="inline-flex items-center gap-2"
    data-testid="avatar"
  >
    <!-- The tooltip anchors to the avatar element only, not to the text column beside it.
         ZoneTooltip is renderless without content, so there is no branch to keep in step. -->
    <ZoneTooltip :content="tooltip">
      <AvatarRoot
        as="span"
        :class="boxClasses"
        data-testid="avatar-box"
      >
        <AvatarImage
          v-if="showImage"
          :src="src"
          :alt="imageAlt"
          :class="mediaClasses"
          @loading-status-change="$emit('image-status', $event)"
        />
        <AvatarFallback
          as="span"
          :class="fallbackClasses"
          :role="accessibleName ? 'img' : undefined"
          :aria-label="accessibleName || undefined"
          :aria-hidden="accessibleName ? undefined : 'true'"
          data-testid="avatar-fallback"
        >
          {{ initials }}
        </AvatarFallback>
        <span
          v-if="showIndicator"
          :class="indicatorClasses"
          data-testid="avatar-indicator"
        >
          <slot name="indicator">
            <ZoneIndicator
              :color="indicatorColor"
              :size="indicatorSize"
              border
            />
          </slot>
        </span>
      </AvatarRoot>
    </ZoneTooltip>

    <div
      v-if="hasTextColumn"
      class="min-w-0"
      data-testid="avatar-text-column"
    >
      <p
        v-if="text || $slots.text"
        :class="textClasses"
        data-testid="avatar-text"
      >
        <slot name="text">{{ text }}</slot>
      </p>
      <p
        v-if="subtext || $slots.subtext"
        :class="subtextClasses"
        data-testid="avatar-subtext"
      >
        <slot name="subtext">{{ subtext }}</slot>
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, type PropType } from 'vue';
import { AvatarRoot, AvatarImage, AvatarFallback } from 'reka-ui';
import ZoneIndicator, { type IndicatorSize, type IndicatorColor } from './Indicator.vue';
import ZoneTooltip from './Tooltip.vue';

const SIZES = ['sm', 'base', 'md', 'lg'] as const;

export type AvatarSize = (typeof SIZES)[number];

const SHAPES = ['round', 'square'] as const;

export type AvatarShape = (typeof SHAPES)[number];

export type AvatarImageStatus = 'idle' | 'loading' | 'loaded' | 'error';

interface AvatarSizeClasses {
  /** Figma: SM 24, Regular 32, MD 48, LG 80. */
  box: string;
  /** Figma "Rounded" corner radius: 4 / 6 / 8 / 12 px. */
  radius: string;
  /** Figma placeholder text: 12 / 12 / 16 / 30 px, Roboto Medium. */
  initials: string;
  /** Not in Figma — carried over from Zone::Avatar's text/subtext slots. */
  text: string;
  subtext: string;
}

const SIZE_CLASSES: Record<AvatarSize, AvatarSizeClasses> = {
  sm: { box: 'h-6 w-6', radius: 'rounded', initials: 'text-xs', text: 'text-xs font-semibold', subtext: 'text-xs' },
  base: { box: 'h-8 w-8', radius: 'rounded-md', initials: 'text-xs', text: 'text-base font-semibold', subtext: 'text-xs' },
  md: { box: 'h-12 w-12', radius: 'rounded-lg', initials: 'text-md', text: 'text-md font-semibold', subtext: 'text-base' },
  lg: { box: 'h-20 w-20', radius: 'rounded-xl', initials: 'text-3xl', text: 'text-3xl font-semibold', subtext: 'text-2xl' },
};

// Where the dot sits. ONE rule per shape, no per-size table: the point a corner dot belongs on
// is a property of the shape's outline, so anything derived from it is right at every size by
// construction. (The per-size translate table this replaces pulled the dot in by a different
// hand-picked amount at each size, and drifted at all of them.)
//
// ROUND  the dot's CENTRE lands on the circumference, on the bottom-right diagonal. That point is
//        r/√2 out from the middle along each axis, so it is 50% − 35.355% = 14.645% in from the
//        box's bottom and right edges — hence `bottom/right-[14.6%]` to put the dot's EDGE there
//        plus a half-its-own-size nudge back out to land its CENTRE on it instead. Half the dot
//        ends up outside the circle and its white ring bites into the face, which is the Figma
//        look.
// SQUARE a rounded rectangle HAS a corner to tuck into, so the dot stays flush with the bottom
//        edge and hangs a quarter of itself off the right one.
const INDICATOR_POSITIONS: Record<AvatarShape, string[]> = {
  round: ['bottom-[14.6%]', 'right-[14.6%]', 'translate-x-1/2', 'translate-y-1/2'],
  square: ['bottom-0', 'right-0', 'translate-x-1/4'],
};

// The dot barely grows with the avatar, so the two small avatars take the 6px dot and the two big
// ones the 8px dot. Scaling it proportionally would put a quarter-of-the-face dot on the 24px
// avatar, which is what a single fixed 8px dot already did.
const INDICATOR_SIZES: Record<AvatarSize, IndicatorSize> = {
  sm: 'sm',
  base: 'sm',
  md: 'base',
  lg: 'base',
};
</script>

<script setup lang="ts">
defineOptions({ name: 'ZoneAvatar' });

const props = defineProps({
  /** Image URL. Omit it (or set `placeholder`) for the initials placeholder. */
  src: {
    type: String,
    default: null,
  },
  /**
   * Drives the initials and, unless `alt` is given, the image alt text. Pass it even with a `src`.
   */
  name: {
    type: String,
    default: null,
  },
  /** Overrides the image alt. Pass an empty string to mark the avatar decorative. */
  alt: {
    type: String,
    default: null,
  },
  /** 24 / 32 / 48 / 80px. */
  size: {
    type: String as PropType<AvatarSize>,
    default: 'base',
    validator: (v: string) => (SIZES as readonly string[]).includes(v),
  },
  /** Square corners scale with size: 4 / 6 / 8 / 12px. */
  shape: {
    type: String as PropType<AvatarShape>,
    default: 'round',
    validator: (v: string) => (SHAPES as readonly string[]).includes(v),
  },
  /** Show the initials even when `src` is set. */
  placeholder: {
    type: Boolean,
    default: false,
  },
  /**
   * 2px white ring, drawn inside the box so the footprint is unchanged. For dark surfaces and for
   * overlapping avatars in a stack.
   */
  border: {
    type: Boolean,
    default: false,
  },
  /**
   * Show the corner status dot ("Notification dot" in Figma). Its size and position derive from the
   * avatar, not from props. The `#indicator` slot implies it.
   */
  indicator: {
    type: Boolean,
    default: false,
  },
  /** Colour of the corner dot. */
  indicatorColor: {
    type: String as PropType<IndicatorColor>,
    default: 'green',
  },
  /** Name line rendered beside the avatar. */
  text: {
    type: String,
    default: null,
  },
  /** Secondary line rendered under `text`. */
  subtext: {
    type: String,
    default: null,
  },
  /**
   * Hover text over the avatar box (ZoneTooltip), anchored to the avatar only, not to the text
   * column. Wrap the avatar in your own ZoneTooltip for rich content.
   */
  tooltip: {
    type: String,
    default: null,
  },
});

defineEmits<{
  /**
   * The image's load state, from Reka's AvatarImage. Only fires when an image is actually rendered. Use
   * 'error' to log broken avatar URLs; the fallback shows by itself.
   */
  'image-status': [status: AvatarImageStatus];
}>();

const slots = defineSlots<{
  /**
   * Replaces the corner dot with your own ZoneIndicator (e.g. `<ZoneIndicator color="red" count="3" border />`).
   * Shows the indicator on its own, no need for `indicator`.
   */
  indicator?(): any;
  /** Markup version of the `text` prop. */
  text?(): any;
  /** Markup version of the `subtext` prop. */
  subtext?(): any;
}>();

const sizeClasses = computed<AvatarSizeClasses>(() => SIZE_CLASSES[props.size]);

const radiusClass = computed<string>(() => (props.shape === 'round' ? 'rounded-full' : sizeClasses.value.radius));

const showImage = computed<boolean>(() => Boolean(props.src) && !props.placeholder);

const showIndicator = computed<boolean>(() => props.indicator || Boolean(slots.indicator));

const hasTextColumn = computed<boolean>(() => Boolean(props.text || props.subtext || slots.text || slots.subtext));

/** The name announced by screen readers. `alt` wins, including an explicit empty string. */
const accessibleName = computed<string>(() => (props.alt ?? props.name) || '');

const imageAlt = computed<string>(() => accessibleName.value);

const initials = computed<string>(() => {
  const words = (props.name || '').trim().split(/\s+/).filter(Boolean);

  if (!words.length) {return '–';}

  const first = words[0][0];
  const last = words.length > 1 ? words[words.length - 1][0] : '';

  return `${first}${last}`.toUpperCase();
});

// No overflow-hidden: the corner indicator is allowed to overhang the box, so the radius is
// applied to the image and the fallback individually instead of being clipped by the parent.
const boxClasses = computed<string[]>(() => {
  const classes = ['relative', 'inline-flex', 'shrink-0', 'items-center', 'justify-center', sizeClasses.value.box, radiusClass.value];

  if (props.border) {classes.push('border-2', 'border-white');}

  return classes;
});

const mediaClasses = computed<string[]>(() => ['h-full', 'w-full', 'object-cover', radiusClass.value]);

const fallbackClasses = computed<string[]>(() => [
  'flex',
  'h-full',
  'w-full',
  'items-center',
  'justify-center',
  'select-none',
  'bg-orange-500',
  'font-medium',
  'leading-none',
  'text-white',
  sizeClasses.value.initials,
  radiusClass.value,
]);

const indicatorClasses = computed<string[]>(() => ['absolute', ...INDICATOR_POSITIONS[props.shape]]);

const indicatorSize = computed<IndicatorSize>(() => INDICATOR_SIZES[props.size]);

const textClasses = computed<string[]>(() => [...sizeClasses.value.text.split(' '), 'truncate', 'text-gray-900']);

const subtextClasses = computed<string[]>(() => [sizeClasses.value.subtext, 'truncate', 'font-regular', 'text-gray-500']);
</script>
